import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { fmtNum, formatDateTime, STATUS_COLORS } from "@/lib/constants";

export default function OrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  useEffect(() => {
    api.get(`/dealer/orders/${id}`).then(({ data }) => setO(data));
  }, [id]);
  if (!o) return <div className="text-[#6b655c]">Yükleniyor…</div>;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="dealer-order-detail">
      <Link to="/bayi/siparislerim" className="inline-flex items-center gap-2 text-sm text-[#6b655c] hover:text-[#c8a97e]">
        <ArrowLeft size={14}/> Siparişlerime dön
      </Link>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-tag">Sipariş</span>
          <h1 className="font-serif text-4xl mt-2">{o.order_no}</h1>
          <p className="text-[#6b655c] mt-1">{formatDateTime(o.created_at)}</p>
        </div>
        <span className={`text-xs px-3 py-1.5 ${STATUS_COLORS[o.status]}`}>{o.status}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#eae5dc] p-6">
          <h3 className="font-serif text-xl mb-4">Sipariş Satırları</h3>
          <div className="space-y-4">
            {o.items.map((it) => (
              <div key={it.id} className="border border-[#eae5dc] p-4">
                <div className="flex justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-medium">{it.product_name}</div>
                    <div className="text-xs text-[#6b655c]">{it.variant_label}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                  {it.product_type === "READY_SIZE" ? (
                    <>
                      <Mini label="Adet" value={it.quantity} />
                      <Mini label="Ebat" value={`${it.selected_width_cm}×${it.selected_height_cm} cm`} />
                    </>
                  ) : (
                    <>
                      <Mini label="En" value={`${it.selected_width_cm} cm`} />
                      <Mini label="Boy" value={`${fmtNum((it.length_meter || 0) * 100)} cm`} />
                    </>
                  )}
                  <Mini label="Toplam Uzunluk" value={`${fmtNum((it.calculated_total_meters || 0) * 100)} cm`} />
                  <Mini label="m²" value={`${fmtNum(it.calculated_sqm)} m²`} />
                </div>
                {it.special_processing && (
                  <div className="mt-3 text-sm bg-[#f7f5f0] p-3 border border-[#eae5dc]">
                    <div className="text-xs uppercase tracking-wider text-[#c8a97e] mb-1">Özel İşlem</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {it.special_processing.shape && <span><strong>Şekil:</strong> {it.special_processing.shape}</span>}
                      {it.special_processing.edge && <span><strong>Kenar:</strong> {it.special_processing.edge}</span>}
                      {it.special_processing.fringe_type && <span><strong>Saçak:</strong> {it.special_processing.fringe_type}</span>}
                    </div>
                    {it.special_processing.note && <div className="mt-1 text-xs text-[#6b655c]">{it.special_processing.note}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-[#eae5dc] p-6">
            <h3 className="font-serif text-lg">Toplam</h3>
            <div className="space-y-2 mt-3 text-sm">
              <Row label="Satır Sayısı" value={o.items.length} />
              <Row label="Toplam Uzunluk" value={`${fmtNum((o.total_meters || 0) * 100)} cm`} />
              <Row label="Toplam m²" value={`${fmtNum(o.total_sqm)} m²`} />
            </div>
          </div>
          <div className="bg-white border border-[#eae5dc] p-6">
            <h3 className="font-serif text-lg">Teslimat</h3>
            <p className="text-sm mt-3 whitespace-pre-wrap">{o.delivery_address}</p>
          </div>
          {o.note && (
            <div className="bg-white border border-[#eae5dc] p-6">
              <h3 className="font-serif text-lg">Sipariş Notu</h3>
              <p className="text-sm mt-3 whitespace-pre-wrap">{o.note}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Mini = ({ label, value }) => (
  <div>
    <div className="text-[0.65rem] uppercase tracking-wider text-[#6b655c]">{label}</div>
    <div className="font-medium mt-0.5">{value}</div>
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex justify-between border-b border-[#f0ebe2] pb-1">
    <span className="text-[#6b655c]">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);
